import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import appleWalletService from '../../services/appleWalletService';
import PrintableTicket from './PrintableTicket';

const WalletButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0;
  
  @media print {
    display: none !important;
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const PrintButton = styled.button`
  background: transparent;
  color: #192f6a;
  border: 1px solid #192f6a;
  border-radius: 6px;
  padding: 0.625rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  min-width: 150px;
  
  &:hover:not(:disabled) {
    background: #192f6a;
    color: white;
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PrintIcon = styled.svg`
  width: 16px;
  height: 16px;
  fill: currentColor;
`;

const HiddenPrintContainer = styled.div`
  position: absolute;
  left: -99999px;
  top: 0;
  opacity: 0;
  pointer-events: none;
  
  @media print {
    position: static !important;
    left: 0 !important;
    opacity: 1 !important;
    pointer-events: auto !important;
  }
`;

const WalletButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1.75rem;
  background: ${props => props.variant === 'google' ? '#fff' : '#000'};
  color: ${props => props.variant === 'google' ? '#3c4043' : '#fff'};
  border: ${props => props.variant === 'google' ? '1px solid #dadce0' : 'none'};
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.variant === 'google' 
    ? '0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15)' 
    : '0 2px 4px rgba(0, 0, 0, 0.2)'};
  min-width: 200px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  
  &:hover:not(:disabled) {
    background: ${props => props.variant === 'google' ? '#f8f9fa' : '#1a1a1a'};
    box-shadow: ${props => props.variant === 'google' 
      ? '0 1px 3px 0 rgba(60,64,67,.3), 0 4px 8px 3px rgba(60,64,67,.15)' 
      : '0 4px 8px rgba(0, 0, 0, 0.3)'};
  }
  
  &:active:not(:disabled) {
    box-shadow: ${props => props.variant === 'google' 
      ? '0 1px 2px 0 rgba(60,64,67,.3)' 
      : '0 1px 2px rgba(0, 0, 0, 0.2)'};
  }
  
  &:disabled {
    background: #f1f3f4;
    color: #80868b;
    border-color: #dadce0;
    cursor: not-allowed;
    box-shadow: none;
    opacity: 0.6;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

// Apple Wallet Icon SVG
const AppleWalletIcon = styled.svg`
  width: 20px;
  height: 20px;
  fill: currentColor;
`;

// Google Wallet Icon - styled "G" in Google colors
const GoogleWalletIcon = styled.div`
  width: 18px;
  height: 18px;
  background: linear-gradient(to bottom, #4285f4 0%, #4285f4 50%, #34a853 50%, #34a853 100%);
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
  color: white;
  font-family: 'Google Sans', sans-serif;
  
  &::before {
    content: 'G';
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${props => props.variant === 'google' ? 'rgba(60,64,67,0.3)' : 'rgba(255,255,255,0.3)'};
  border-radius: 50%;
  border-top-color: ${props => props.variant === 'google' ? '#3c4043' : '#fff'};
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const StatusMessage = styled.div`
  font-size: 0.9rem;
  text-align: center;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  max-width: 400px;
  
  &.success {
    background: rgba(46, 204, 113, 0.1);
    color: #27ae60;
    border: 1px solid rgba(46, 204, 113, 0.3);
  }
  
  &.error {
    background: rgba(231, 76, 60, 0.1);
    color: #c0392b;
    border: 1px solid rgba(231, 76, 60, 0.3);
  }
  
  &.info {
    background: rgba(52, 152, 219, 0.1);
    color: #2980b9;
    border: 1px solid rgba(52, 152, 219, 0.3);
  }
`;

const InfoText = styled.p`
  font-size: 0.85rem;
  color: #666;
  text-align: center;
  margin-top: 0.5rem;
  max-width: 400px;
`;

const WalletButtons = ({ 
  bookingData,
  selectedTickets,
  selectedDate,
  selectedTimeSlot,
  customerDetails,
  totalAmount,
  venposBasket,
  bookingSummary,
  pageId = null,
  isFamilySaver = false,
  onSuccess,
  onError,
  disabled = false,
  className = ''
}) => {
  const [isLoadingApple, setIsLoadingApple] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [serverAvailable, setServerAvailable] = useState(true);
  const printRef = useRef(null);

  const PASS_SERVER_URL = 'https://considerate-compassion-production.up.railway.app';

  // Check server availability on mount
  useEffect(() => {
    const checkServer = async () => {
      try {
        const response = await fetch(`${PASS_SERVER_URL}/`);
        const data = await response.json();
        setServerAvailable(response.ok);
        
        if (!response.ok) {
          setStatus({
            type: 'error',
            message: 'Pass service is currently unavailable. Please try again later.'
          });
        }
      } catch (error) {
        setServerAvailable(false);
        setStatus({
          type: 'error',
          message: 'Unable to connect to pass service.'
        });
      }
    };
    
    checkServer();
  }, []);

  const handleAddToAppleWallet = () => {
    if (!bookingData) {
      setStatus({
        type: 'error',
        message: 'Booking information is missing.'
      });
      return;
    }

    setIsLoadingApple(true);
    setStatus({ type: '', message: '' });

    try {
      // Build URL synchronously so we stay inside the iOS user-gesture.
      // Awaiting before navigation can make Safari cancel the .pkpass open.
      const downloadUrl = appleWalletService.generateDirectDownloadUrlFromBooking(bookingData);

      setStatus({
        type: 'success',
        message: 'Opening Apple Wallet pass...'
      });

      if (onSuccess) {
        onSuccess('apple', downloadUrl);
      }

      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (isIOS) {
        // Same-tab navigation is required for iOS to hand .pkpass to Wallet
        window.location.assign(downloadUrl);
      } else {
        // Keep confirmation page open on desktop; file still downloads
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.rel = 'noopener';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to generate Apple Wallet pass'
      });
      
      if (onError) {
        onError('apple', error);
      }
    } finally {
      setIsLoadingApple(false);
    }
  };

  const handleAddToGoogleWallet = async () => {
    if (!bookingData) {
      setStatus({
        type: 'error',
        message: 'Booking information is missing.'
      });
      return;
    }

    setIsLoadingGoogle(true);
    setStatus({ type: '', message: '' });

    try {

      const {
        bookingReference,
        eventName = 'Belvoir Castle Visit',
        selectedDate,
        selectedTimeSlot,
        selectedTickets,
        customerDetails
      } = bookingData;

      // Format data for Google Wallet
      // Format date in local timezone to avoid UTC conversion issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const eventDate = `${year}-${month}-${day}`; // YYYY-MM-DD format in local timezone
      const eventTime = appleWalletService.formatTimeSlot(selectedTimeSlot);
      const quantity = selectedTickets
        .filter(ticket => ticket.quantity > 0)
        .reduce((total, ticket) => total + ticket.quantity, 0);

      const ticketData = {
        TicketReference: bookingReference,
        EventName: eventName,
        EventDate: eventDate,
        EventTime: eventTime,
        Quantity: quantity.toString(),
        CustomerName: `${customerDetails.firstName} ${customerDetails.lastName}`
      };

      const response = await fetch(`${PASS_SERVER_URL}/api/google/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketData)
      });

      if (!response.ok) {
        throw new Error('Failed to generate Google Wallet pass');
      }

      const data = await response.json();
      
      if (data.success && data.saveUrl) {
        // Open Google Wallet save URL in new window
        window.open(data.saveUrl, '_blank');
        
        setStatus({
          type: 'success',
          message: 'Opening Google Wallet...'
        });
        
        if (onSuccess) {
          onSuccess('google', data.saveUrl);
        }
      } else {
        throw new Error(data.error || 'Failed to generate Google Wallet pass');
      }
      
    } catch (error) {

      setStatus({
        type: 'error',
        message: error.message || 'Failed to generate Google Wallet pass'
      });
      
      if (onError) {
        onError('google', error);
      }
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handlePrint = () => {


    if (!bookingData) {

      setStatus({
        type: 'error',
        message: 'Booking information is missing.'
      });
      return;
    }

    try {

      // Use bookingData as primary source, fallback to individual props
      const resolvedPageId = pageId || bookingSummary?.pageId || bookingData?.bookingSummary?.pageId || venposBasket?.data?.pageId || null;
      const mergedBookingSummary = {
        ...(bookingSummary || bookingData.bookingSummary || {}),
        ...(resolvedPageId ? { pageId: resolvedPageId } : {}),
      };

      const ticketData = {
        bookingReference: bookingData.bookingReference || 'TEST-REF',
        selectedTickets: bookingData.selectedTickets || selectedTickets || [],
        selectedDate: bookingData.selectedDate ? bookingData.selectedDate.toISOString() : (selectedDate ? selectedDate.toISOString() : new Date().toISOString()),
        selectedTimeSlot: bookingData.selectedTimeSlot || selectedTimeSlot || {},
        customerDetails: bookingData.customerDetails || customerDetails || {},
        totalAmount: bookingData.totalAmount || totalAmount || 0,
        venposBasket: venposBasket || bookingData.venposBasket || null,
        bookingSummary: Object.keys(mergedBookingSummary).length > 0 ? mergedBookingSummary : null,
        isFamilySaver: bookingData.isFamilySaver || isFamilySaver || false
      };


      // Store in localStorage (more reliable than sessionStorage for popup windows)
      const storageKey = `ticket_${Date.now()}`;
      localStorage.setItem(storageKey, JSON.stringify(ticketData));


      // Open ticket page with storage key
      const ticketWindow = window.open(`/print-ticket?key=${storageKey}`, '_blank');
      
      if (!ticketWindow) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      setStatus({
        type: 'success',
        message: 'Opening ticket in new window...'
      });
      
      // Clean up old storage after 5 minutes
      setTimeout(() => {
        localStorage.removeItem(storageKey);
      }, 300000);
      
    } catch (error) {

      setStatus({
        type: 'error',
        message: error.message || 'Failed to open ticket window'
      });
    }
  };

  const isDisabled = disabled || !serverAvailable || !bookingData;

  return (
    <>
      <WalletButtonsContainer className={className + ' no-print'}>
        <InfoText>Add your tickets to your digital wallet for easy access:</InfoText>
        
        <ButtonsRow>
          {/* Apple Wallet Button */}
          <WalletButton 
            variant="apple"
            onClick={handleAddToAppleWallet}
            disabled={isDisabled || isLoadingApple}
            title="Add tickets to Apple Wallet"
          >
            {isLoadingApple ? (
              <LoadingSpinner variant="apple" />
            ) : (
              <AppleWalletIcon viewBox="0 0 24 24">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </AppleWalletIcon>
            )}
            {isLoadingApple ? 'Generating...' : 'Add to Apple Wallet'}
          </WalletButton>

          {/* Google Wallet Button */}
          <WalletButton 
            variant="google"
            onClick={handleAddToGoogleWallet}
            disabled={isDisabled || isLoadingGoogle}
            title="Add tickets to Google Wallet"
          >
            {isLoadingGoogle ? (
              <LoadingSpinner variant="google" />
            ) : (
              <GoogleWalletIcon />
            )}
            {isLoadingGoogle ? 'Generating...' : 'Add to Google Wallet'}
          </WalletButton>
        </ButtonsRow>

        {/* Print Ticket Button */}
        <PrintButton 
          onClick={handlePrint}
          disabled={isDisabled}
          title="Print your ticket"
        >
          <PrintIcon viewBox="0 0 24 24">
            <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
          </PrintIcon>
          Print Ticket
        </PrintButton>
        
        {status.message && (
          <StatusMessage className={status.type}>
            {status.message}
          </StatusMessage>
        )}
        
        {!serverAvailable && !status.message && (
          <StatusMessage className="error">
            Digital wallet service is temporarily unavailable
          </StatusMessage>
        )}
      </WalletButtonsContainer>

    </>
  );
};

export default WalletButtons;

